import React from "react";
import { TD, TH } from "styled/table";
import Modal from "components/Modal";
import { Button } from "styled";
import { useLocation } from "react-router-dom";
import { trackDownloadAndBulkUploadEvents } from "utils/analytics";
import { ANALYTICS_EVENTS } from "utils/analyticsEvents";

/** "totalKillometerTravelled" -> "Total Killometer Travelled", "ITM km" -> "ITM km".
 *  Column headers come straight off the API's object keys, so split camelCase
 *  into words; <TH> upper-cases them, and words wrap far better than one long run. */
const humanizeHeader = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

/** A column is numeric when at least one row holds a number for it - the header
 *  then has to be right-aligned too, or it floats away from its own data. */
const isNumericColumn = (rows, key) =>
  rows.some((row) => typeof row?.[key] === "number");

const ModalTable = ({ modalData, setModalData, exportExcel }) => {
  const rows = modalData?.data ?? [];
  const columns = Object.keys(rows[0] ?? {});
  const numericColumns = new Set(
    columns.filter((key) => isNumericColumn(rows, key))
  );
  const location = useLocation();

  return (
    <>
      {modalData !== null && (
        <Modal
          handleClick={() => setModalData(null)}
          title={modalData.title}
          className="max-w-2xl"
        >

          <h1 className="w-full flex justify-end text-end px-2 my-1 sticky h-10">
            <Button
              onClick={() => {
                trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.CLICK_DOWNLOAD_BUTTON, "Mobility Technical", location.pathname, { model_name: modalData.title });
                try {
                  const headerKeys = Object.keys(modalData?.data[0] ?? {});

                  const columnToFooterMap = {
                      "Original NSD": "Avg Original NSD",
                      averageNSD: "Avg NSD",
                      "Percentage Wear": "Avg Percentage Wear",
                      totalKillometerTravelled: "Avg TotalKm",
                      projectionKm: "Avg Projection KM",
                    };

                    const footerRow = {};

                    headerKeys.forEach((key) => {
                      const mappedFooterKey = columnToFooterMap[key];
                      if (mappedFooterKey) {
                        const footerItem = modalData?.footer?.find(
                          (f) => f.title === mappedFooterKey
                        );
                        footerRow[key] = footerItem ? footerItem.value : "";
                      } else {
                        footerRow[key] = key === Object.keys(modalData.data[0])[0] ? "Total/Avg" : "";
                      }
                    });

                  exportExcel(
                    [
                      ...modalData.data,
                      modalData.footer ? footerRow : {},
                    ],
                    modalData.title.split(" ").join("_") + "_Data"
                  );
                  trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.SYS_DOWNLOAD_SUCCESS, "Mobility Technical", location.pathname, { model_name: modalData.title });
                } catch (error) {
                  trackDownloadAndBulkUploadEvents(ANALYTICS_EVENTS.SYS_DOWNLOAD_ERROR, "Mobility Technical", location.pathname, { model_name: modalData.title, errMsg: error?.message });
                }
              }}
              className="text-blue-500 hover:scale-105 cursor-pointer text-right"
            >
              Download
            </Button>
          </h1>

          <div className="max-h-[60vh] overflow-auto">
            {/* table-auto (not table-fixed): with fixed layout every column got an
                equal slice of the modal, so long headers such as
                TOTALKILLOMETERTRAVELLED were crushed while short ones sat in
                whitespace. Auto layout sizes each column to its content and the
                wrapper above scrolls horizontally when the table is wider. */}
            <table className="min-w-full table-auto">
              <thead className="bg-gray-50 dashboard-table-header">
                <tr>
                  <TH scope="col" className="whitespace-nowrap">
                    S.N.
                  </TH>
                  {columns.map((key) => (
                    <TH
                      key={key}
                      scope="col"
                      className={`whitespace-nowrap ${
                        numericColumns.has(key) ? "!text-right" : ""
                      }`}
                    >
                      {humanizeHeader(key)}
                    </TH>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {rows.map((item, index) => (
                  <tr key={index}>
                    <TD>{index + 1}</TD>
                    {columns.map((key) => (
                      <TD
                        key={key}
                        className={
                          numericColumns.has(key) ? "text-right" : "text-left"
                        }
                      >
                        {typeof item[key] === "boolean"
                          ? item[key]
                            ? "Yes"
                            : "No"
                          : item[key]}
                      </TD>
                    ))}
                  </tr>
                ))}
              </tbody>

              {/* Table Footer (Totals/Avg) */}
              {modalData.footer && Array.isArray(modalData.footer) && (
                <tfoot className="bg-gray-50 dashboard-table-footer">
                  <tr className="font-medium text-gray-700">
                    <td className="py-2 px-3">
                      Totals/Avg
                    </td>

                    {columns.map((key) => {
                      const columnToFooterMap = {
                        "Original NSD": "Avg Original NSD",
                        averageNSD: "Avg NSD",
                        "Percentage Wear": "Avg Percentage Wear",
                        totalKillometerTravelled: "Avg TotalKm",
                        projectionKm: "Avg Projection KM",
                      };

                      const mappedFooterKey = columnToFooterMap[key];
                      const footerItem = modalData.footer.find(
                        (f) => f.title === mappedFooterKey
                      );

                      return (
                        <td
                          key={key}
                          className={`py-2 px-2 ${
                            numericColumns.has(key) ? "text-right" : "text-left"
                          }`}
                        >
                          {footerItem ? footerItem.value : ""}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

        </Modal>
      )}
    </>
  );
};

export default ModalTable;
